import { FileText } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const DEFAULT_METRICS_PLACEHOLDER =
  "Revenue (FY25) | ₹92,000 Cr\nPAT | ₹12,400 Cr\nEBITDA | ₹18,600 Cr\nEBITDA Margin | 20.2%\nROE | 15.4%\nROCE | 21.3%\nDebt / Equity | 0.4x\nP/E | 24.5x";

function FileField({ label, accept, mediaId, uploading, onUpload, testid, isImage }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input data-testid={testid} type="file" accept={accept} onChange={onUpload} className="text-sm flex-1" />
        {uploading && <span className="text-xs text-gray-400">Uploading...</span>}
        {mediaId && !uploading && (
          isImage
            ? <img src={`${API}/files/${mediaId}`} alt="" className="h-9 w-9 rounded object-cover border border-gray-200" />
            : <span className="inline-flex items-center gap-1 text-xs text-green-700"><FileText className="h-3.5 w-3.5" /> attached</span>
        )}
      </div>
    </div>
  );
}

/** All the input fields for the report create/edit form — pure rendering, no state of its own. */
export function ReportFormFields({ form, setForm, uploads, onUpload }) {
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Company Name</Label>
          <Input data-testid={ADMIN.formCompanyName} value={form.company_name} onChange={set("company_name")} placeholder="Wakefit" />
        </div>
        <div className="space-y-1.5">
          <Label>NSE/BSE Symbol <span className="text-gray-400 font-normal">(optional)</span></Label>
          <Input data-testid={ADMIN.formSymbol} value={form.symbol}
            onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} placeholder="WAKEFIT" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Sector</Label>
          <Input data-testid={ADMIN.formSector} value={form.sector} onChange={set("sector")} placeholder="Consumer" />
        </div>
        <div className="space-y-1.5">
          <Label>Industry <span className="text-gray-400 font-normal">(optional)</span></Label>
          <Input data-testid={ADMIN.formIndustry} value={form.industry} onChange={set("industry")} placeholder="Home Furnishings" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label>KIRA Score (0–100)</Label>
          <Input data-testid={ADMIN.formScore} type="number" min={0} max={100} value={form.kira_score} onChange={set("kira_score")} placeholder="78" />
        </div>
        <div className="space-y-1.5">
          <Label>Recommendation</Label>
          <select data-testid={ADMIN.formRecommendation} value={form.recommendation} onChange={set("recommendation")}
            className="h-9 w-full rounded-md border border-gray-200 bg-white px-2.5 text-sm text-gray-700">
            <option value="Buy">Buy</option>
            <option value="Hold">Hold</option>
            <option value="Sell">Sell</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Report Date</Label>
          <Input data-testid={ADMIN.formDate} type="date" value={form.report_date} onChange={set("report_date")} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Current Price <span className="text-gray-400 font-normal">(optional snapshot)</span></Label>
          <Input data-testid={ADMIN.formCurrentPrice} value={form.current_price} onChange={set("current_price")} placeholder="₹2,845.30" />
        </div>
        <div className="space-y-1.5">
          <Label>Market Cap <span className="text-gray-400 font-normal">(optional snapshot)</span></Label>
          <Input data-testid={ADMIN.formMarketCap} value={form.market_cap} onChange={set("market_cap")} placeholder="₹19.2 L Cr" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Short Investment Summary</Label>
        <Textarea data-testid={ADMIN.formSummary} rows={3} value={form.summary} onChange={set("summary")}
          placeholder="Two or three lines on why this score, key strengths and risks..." />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Strengths <span className="text-gray-400 font-normal">(one per line)</span></Label>
          <Textarea data-testid={ADMIN.formStrengths} rows={4} value={form.strengths_text} onChange={set("strengths_text")}
            placeholder={"Market leader in premium sleep\nDebt-free balance sheet\nStrong pricing power"} />
        </div>
        <div className="space-y-1.5">
          <Label>Risks <span className="text-gray-400 font-normal">(one per line)</span></Label>
          <Textarea data-testid={ADMIN.formRisks} rows={4} value={form.risks_text} onChange={set("risks_text")}
            placeholder={"Raw material price volatility\nHeavy dependence on urban demand\nRegulatory risk"} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Key Financial Metrics <span className="text-gray-400 font-normal">(one per line: <code className="text-[11px]">Label | Value</code>)</span></Label>
        <Textarea data-testid={ADMIN.formMetrics} rows={8} value={form.metrics_text} onChange={set("metrics_text")}
          placeholder={DEFAULT_METRICS_PLACEHOLDER} />
        <p className="text-[11px] text-gray-500">
          Use any standard set (Revenue, PAT, EBITDA, ROE, ROCE, D/E, P/E) or add custom rows below.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label>Additional Highlights <span className="text-gray-400 font-normal">(optional — one per line)</span></Label>
        <Textarea data-testid={ADMIN.formHighlights} rows={3} value={form.highlights_text} onChange={set("highlights_text")}
          placeholder={"Revenue up 34% YoY with improving gross margins\nMarket leader in the premium sleep category"} />
      </div>
      <FileField label="Report PDF" accept="application/pdf" mediaId={form.pdf_media_id}
        uploading={uploads.pdf_media_id} onUpload={onUpload("pdf_media_id")}
        testid={ADMIN.formPdfUpload} isImage={false} />
      <div className="grid grid-cols-2 gap-4">
        <FileField label="Company Logo" accept="image/*" mediaId={form.logo_media_id}
          uploading={uploads.logo_media_id} onUpload={onUpload("logo_media_id")}
          testid={ADMIN.formLogoUpload} isImage />
        <FileField label="Cover Image" accept="image/*" mediaId={form.cover_media_id}
          uploading={uploads.cover_media_id} onUpload={onUpload("cover_media_id")}
          testid={ADMIN.formCoverUpload} isImage />
      </div>
    </div>
  );
}
